import { useLoaderData, useNavigate } from "react-router-dom";
import { copyToClipboard } from "../../lib/utils";
import { useToast } from "../../components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { ActionsContext } from "../../lib/actions";
import { useSearchParams } from "react-router-dom";
import { fetch } from "@/src/lib/fetch";
import { cn } from "../../lib/utils";
import { BoardingInfo, Booking, GoodToKnow, Operator as OperatorModel } from "./model";
import { Overview } from "./Tabs/Overview";
import { Operator } from "./Tabs/Operator";
import { Payments } from "./Tabs/Payments";
import { XeroSection } from "./Tabs/XeroSection";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBookingCharterDataQuery } from "@/src/lib/bookingByYachtOfferQuery";
import { useBookingsStore } from "../../stores/bookingsStore";
import { useOffersStore } from "../../stores/offersStore";
import { Currency } from "../Currencies/model";
import { Button } from "../../components/ui/button";
import { LinkYachtDialog } from "./LinkYachtDialog";
import BoardingSection from "./Tabs/sections/BoardingSection";
import GoodToKnowSection from "./Tabs/sections/GoodToKnowSection";
import { CommissionSection } from "./Tabs/sections/CommissionSection";
import { SupplierBankSection } from "./Tabs/sections/SupplierBankSection";
import { DocumentsSection } from "./Tabs/sections/DocumentsSection";
import { extractFromMultiName } from "../Locations/utils";
import { BookingHeader } from "./BookingHeader";
import { charterProfile, charterStarted, resolveTab, tabsFor } from "./charterProfile";
import { ConfirmTab, ConfirmationSummary, useConfirmation } from "./ConfirmTab";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { BookingActionBar } from "./BookingActionBar";
import { BookingRail } from "./BookingRail";
import { AlertsStrip, useBrief } from "./Tabs/sections/CharterBriefCard";
import { useUserStore, workspaceRole } from "@/src/stores/userStore";
import TransferSection from "./Tabs/sections/TransferSection";
import ContractSection from "./Tabs/sections/ContractSection";
import { ReviewSection } from "./Tabs/sections/ReviewSection";
import { PreferencesSection } from "./Tabs/sections/PreferencesSection";
import { CommsSection } from "./Tabs/sections/CommsSection";
import { CharterOpsSection } from "./Tabs/sections/CharterOpsSection";
import { TimelineSection } from "./Tabs/sections/TimelineSection";
import { useBookingData } from "@/src/hooks/useBookingData";

/**
 * One scrollable booking record (workspace integration Phase B, ws-booking.js):
 * the body is tabbed (v14 brief) behind the sticky ready header, reading
 * `?tab=`. Legacy `?tab=` values — the ops page's brief / documents /
 * checklist and this page's former section anchors — resolve to tabs.
 */
// Section list is charter-type driven — see charterProfile.ts.

export const BookingDetails = () => {
  const [yachtDetailsOpen, setYachtDetailsOpen] = useState(false);
  const [yachtId, setYachtId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  // Tabs (v14): the URL is the source of truth; legacy anchors/aliases resolve.
  const activeSection = resolveTab(searchParams.get("tab"));

  const { setCurrencies, currencies } = useOffersStore();
  function fetchCurrencies() {
    return fetch<{ data: Currency[] }>("/api/currencies").then((res) => setCurrencies(res.data ?? []));
  }
  useEffect(() => {
    if (!currencies?.length) {
      fetchCurrencies();
    }
  }, []);
  const initialData = useLoaderData() as Booking;
  const { data: booking, isLoading, error } = useBookingCharterDataQuery(initialData?.id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setBooking } = useBookingsStore();

  useEffect(() => {
    if (booking) setBooking(booking);
  }, [booking]);


  const handleCopyToClipboard = (text: string) => {
    copyToClipboard(text);
    toast({
      title: "Copied!",
      variant: "default",
    });
  };

  const goToSection = (id: string) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set("tab", resolveTab(id));
    navigate(`?${updatedSearchParams.toString()}`, { replace: true });
    document.querySelector<HTMLElement>("[data-booking-body]")?.scrollTo({ top: 0 });
  };

  const handleYachtDetailsModal = (id: string) => {
    setYachtId(id);
    setYachtDetailsOpen(true);
  };

  const charterType = booking?.offer?.charterType;
  const profile = useMemo(() => (booking ? charterProfile(booking) : null), [booking]);
  // Confirm tab: pre-booking only, ops-side roles only (helm-app has roles, not
  // ops levels — level 2+ is approximated as ops / finance / admin). When
  // hidden, the confirmation summary folds into Overview so it's never lost.
  const user = useUserStore((st) => st.user);
  const confirmation = useConfirmation(booking?.id ?? "");
  const { brief } = useBrief(booking?.id ?? "");
  const opsSide = ["ops", "finance", "admin"].includes(workspaceRole(user));
  const showConfirmTab = !!confirmation?.preBooking && opsSide;
  const SECTIONS = useMemo(() => {
    if (!booking) return [];
    const tabs = tabsFor(booking);
    if (showConfirmTab) tabs.splice(1, 0, { id: "confirm", label: "Confirm booking" });
    return tabs;
  }, [booking, showConfirmTab]);
  const tab = SECTIONS.some((t) => t.id === activeSection) ? activeSection : "overview";
  // Bank details gate: the supplier's transfer details, or what the contract
  // parse stored on the booking.
  const { operatorData } = useBookingData(booking ?? ({ id: "" } as Booking));
  const bankDetailsOnFile = !!(
    booking?.operatorTransfer?.iban ||
    booking?.operatorTransfer?.accountNumber ||
    operatorData?.transferDetails?.iban ||
    operatorData?.transferDetails?.accountNumber ||
    (booking?.convertDetails as any)?.stakeholderBank?.iban ||
    (booking?.convertDetails as any)?.stakeholderBank?.accountNumber
  );

  // Imported bookings (materialised from HubSpot won deals) have no offer /
  // yacht / customer relations — the full sectioned record would crash on
  // them. Show the deal snapshot until the record is enriched.
  if (booking?.source === "imported" && !(booking as any).offerYacht) {
    return (
      <ActionsContext>
        <ImportedBookingView booking={booking} />
      </ActionsContext>
    );
  }

  // ActionsContext (not WithActionBar): the extras section still uses the
  // emit/subscribe bus, but WithActionBar's nested ScrollArea made Content a
  // non-scrolling overflow ancestor, which is what kept the section nav from
  // sticking. With Content as the real scroller, sticky top-0 works.
  // Two bands, not one scroller (Simon, 21 Sep): the header is a fixed top
  // band that nothing can scroll above or show through, and the body scrolls
  // beneath it — so the rail can be sticky inside the body.
  return (
    <ActionsContext>
      <div className="flex h-full w-full flex-col">
        {booking && (
          <div className="shrink-0">
            <div className="flex items-center justify-between px-6 pt-3">
              <button type="button" onClick={() => navigate("/bookings")} className="flex items-center gap-1 text-sm text-slate-500 hover:underline">
                ‹ Bookings
              </button>
              <div className="flex items-center gap-2">
                {charterType && <span className="rounded-[25px] bg-slate-100 px-3 py-1 text-xs capitalize text-slate-700">{charterType}</span>}
                {booking.brand && <span className={`rounded-[25px] px-3 py-1 text-xs capitalize text-white ${booking.brand === "helm" ? "bg-[#0000dc]" : "bg-[#e9695e]"}`}>{booking.brand}</span>}
              </div>
            </div>
            <BookingHeader
              booking={booking}
              sections={SECTIONS}
              activeSection={activeSection}
              onGoToSection={goToSection}
              bankDetailsOnFile={bankDetailsOnFile}
            />
          </div>
        )}
        <div data-booking-body className="min-h-0 flex-1 overflow-y-auto px-6 pb-10 pt-6">
          {booking && (
            <ErrorBoundary label="Booking page">
            <div className="grid w-full grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="flex min-w-0 flex-col gap-6">
              <AlertsStrip brief={brief} onFocus={() => goToSection("ops")} />
              <BookingActionBar booking={booking} preBooking={showConfirmTab} onConfirm={() => goToSection("confirm")} onCopy={handleCopyToClipboard} />
              {tab === "overview" && (
                <Overview
                  booking={booking}
                  handleCopyToClipboard={handleCopyToClipboard}
                  handleYachtDetailsModal={handleYachtDetailsModal}
                  yachtDetailsOpen={yachtDetailsOpen}
                  yachtId={yachtId}
                  setYachtDetailsOpen={setYachtDetailsOpen}
                  confirmation={confirmation}
                  showConfirmTab={showConfirmTab}
                  onGo={goToSection}
                />
              )}
              {tab === "confirm" && <ConfirmTab c={confirmation} bookingId={booking.id} />}
              {tab === "money" && (
                <>
                  <Payments booking={booking} handleCopyToClipboard={handleCopyToClipboard} />
                  <XeroSection bookingId={booking.id} />
                  {/* The supplier side (details, pricing, bank transfer, notes) — the design keeps it with the money, not on its own tab. */}
                  <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{profile?.supplierLabel ?? "Operator"} — details</p>
                  <Operator booking={booking} handleCopyToClipboard={handleCopyToClipboard} />
                </>
              )}
              {tab === "commission" && <CommissionSection booking={booking} />}
              {tab === "contract" && (
                <>
                  <ContractSection booking={booking} />
                  <DocumentsSection bookingId={booking.id} hasDeal={!!booking.hubspotId} />
                  {profile?.needsBankDetails && <SupplierBankSection bookingId={booking.id} supplierLabel={profile.supplierLabel} />}
                </>
              )}
              {tab === "ops" && (
                <>
                  {profile?.showCharterOps && <CharterOpsSection bookingId={booking.id} hasDeal={!!booking.hubspotId} />}
                  {profile?.showPreferences && <PreferencesSection bookingId={booking.id} hasDeal={!!booking.hubspotId} />}
                </>
              )}
              {tab === "comms" && <CommsSection bookingId={booking.id} />}
              {tab === "boarding" && (
                <>
                  <BoardingSection booking={booking} operator={operatorData} />
                  <TransferSection booking={booking} />
                </>
              )}
              {tab === "review" && <ReviewSection bookingId={booking.id} />}
              {tab === "timeline" && <TimelineSection bookingId={booking.id} />}
            </div>
            <div className="xl:sticky xl:top-0">
              <BookingRail booking={booking} operatorName={operatorData?.name} supplierLabel={profile?.supplierLabel ?? "Operator"} onGo={goToSection} />
            </div>
            </div>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </ActionsContext>
  );
};

function ImportedBookingView({ booking }: { booking: Booking }) {
  const imp = booking.importDetails ?? {};
  const queryClient = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const linkedYachtId = booking.linkedYacht?.id ?? null;
  // Same query key/shape as useBookingData, so the operator cache is shared.
  const { data: operatorRes } = useQuery({
    queryKey: ["operatorData", linkedYachtId],
    queryFn: () => fetch<{ operator: OperatorModel }>(`/api/operators/by-yacht/${linkedYachtId}`),
    enabled: !!linkedYachtId,
  });
  const operator = operatorRes?.operator;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["bookingYachtOfferData", booking.id] });
  const [converting, setConverting] = useState(false);
  const importedUserForConvert = useUserStore((st) => st.user);
  const canConvert = ["ops", "finance", "admin"].includes(workspaceRole(importedUserForConvert));
  const convert = () => {
    if (!window.confirm(`Convert to a full booking page on ${booking.linkedYacht?.name ?? "the linked yacht"}?\n\nThis creates the offer and yacht record behind the page, prefills the money from the HubSpot deal (price, VAT, APA, instalments) and applies payments as Xero shows them. Nothing is posted to Xero or HubSpot.`)) return;
    setConverting(true);
    fetch<{ data: any }>(`/api/bookings/${booking.id}/convert-to-full`, "POST", {})
      .then(() => { refresh(); queryClient.invalidateQueries({ queryKey: ["booking", booking.id] }); queryClient.invalidateQueries(); })
      .catch((e: any) => window.alert(e?.message ?? "Couldn't convert this booking."))
      .finally(() => setConverting(false));
  };
  const unlink = () => {
    if (!window.confirm("Unlink this yacht? Boarding information and good to know will disappear, and the booking won't be auto-linked again.")) return;
    setUnlinking(true);
    fetch(`/api/bookings/${booking.id}/unlink-yacht`, "POST")
      .then(() => refresh())
      .finally(() => setUnlinking(false));
  };
  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
  const rows: [string, React.ReactNode][] = [
    ["Deal", imp.dealName ?? "—"],
    ["Yacht", imp.yachtName ?? "—"],
    ["Charter type", imp.charterType ?? "—"],
    ["Client", `${booking.customerFirstName ?? ""} ${booking.customerLastName ?? ""}`.trim() || "—"],
    ["Email", booking.customerEmail || "—"],
    ["Phone", booking.customerMobile || "—"],
    ["Dates", `${fmtDate(booking.startDate)} → ${fmtDate(booking.endDate)}`],
    [
      "Deal amount",
      imp.dealAmount != null && imp.dealAmountCurrency
        ? new Intl.NumberFormat("en-EN", { style: "currency", currency: imp.dealAmountCurrency }).format(Number(imp.dealAmount))
        : "—",
    ],
    ["Country", imp.charterCountry ?? "—"],
    ["Supplier", [imp.supplierCompanyName, imp.supplierPlatform].filter(Boolean).join(" · ") || "—"],
    ["Charter account", imp.helmDealId ?? "—"],
    ["Status", booking.hubspotStatus?.split("_").join(" ") ?? "—"],
  ];
  // Tabs for imported bookings too (v14): the deal snapshot is the Overview
  // pane; every pane that works by booking id is offered. Sections needing an
  // offer / offer-yacht (extras, boarding, the operator section) stay off.
  const [importedParams] = useSearchParams();
  const importedNavigate = useNavigate();
  const importedUser = useUserStore((st) => st.user);
  const importedConfirmation = useConfirmation(booking.id);
  const { brief: importedBrief } = useBrief(booking.id);
  const importedProfile = charterProfile(booking);
  const importedOpsSide = ["ops", "finance", "admin"].includes(workspaceRole(importedUser));
  const importedShowConfirm = !!importedConfirmation?.preBooking && importedOpsSide;
  const importedTabs = [
    { id: "overview", label: "Overview" },
    ...(importedShowConfirm ? [{ id: "confirm", label: "Confirm booking" }] : []),
    { id: "money", label: "Money" },
    { id: "commission", label: "Commission" },
    { id: "contract", label: "Contract" },
    ...(importedProfile.showCharterOps || importedProfile.showPreferences ? [{ id: "ops", label: "Charter ops" }] : []),
    { id: "comms", label: "Comms" },
    ...(charterStarted(booking) ? [{ id: "review", label: "Review" }] : []),
    { id: "timeline", label: "Timeline" },
  ];
  const importedActive = resolveTab(importedParams.get("tab"));
  const importedTab = importedTabs.some((t) => t.id === importedActive) ? importedActive : "overview";
  const importedGo = (id: string) => {
    const next = new URLSearchParams(importedParams);
    next.set("tab", resolveTab(id));
    importedNavigate(`?${next.toString()}`, { replace: true });
    window.scrollTo({ top: 0 });
  };
  const importedBank = !!(
    (booking.convertDetails as any)?.stakeholderBank?.iban || (booking.convertDetails as any)?.stakeholderBank?.accountNumber ||
    operator?.transferDetails?.iban || operator?.transferDetails?.accountNumber
  );
  return (
    <div className="flex h-full w-full flex-col">
      <div className="shrink-0">
        <div className="flex items-center justify-between px-6 pt-3">
          <button type="button" onClick={() => importedNavigate("/bookings")} className="flex items-center gap-1 text-sm text-slate-500 hover:underline">‹ Bookings</button>
          <span className="rounded-[25px] bg-amber-100 px-3 py-1 text-xs text-amber-800">Imported deal</span>
        </div>
        <BookingHeader booking={booking} sections={importedTabs} activeSection={importedTab} onGoToSection={importedGo} bankDetailsOnFile={importedBank} />
      </div>
      <div data-booking-body className="min-h-0 flex-1 overflow-y-auto px-6 pb-10 pt-6">
      <div className="grid w-full grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          <AlertsStrip brief={importedBrief} onFocus={() => importedGo("ops")} />
          <BookingActionBar booking={booking} preBooking={importedShowConfirm} onConfirm={() => importedGo("confirm")} onCopy={(t) => { copyToClipboard(t); }} />
          {importedTab === "overview" && (
            <>
              {!importedShowConfirm && importedConfirmation?.confirmation && <ConfirmationSummary c={importedConfirmation} />}
    <div className="flex w-full flex-col gap-4">
      <p className="text-sm text-slate-500">
        This booking was imported from HubSpot deal <span className="font-medium">{booking.hubspotId}</span> by the
        finance backfill. Its charter, payment and Xero records link here. Link its yacht & base to bring in boarding
        information and good to know.
      </p>
      <div className="rounded-xl border bg-white">
        <table className="w-full text-sm">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label} className="border-t first:border-t-0">
                <td className="w-44 px-4 py-2 text-slate-500">{label}</td>
                <td className="px-4 py-2">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Yacht & base</p>
            {booking.linkedYacht ? (
              <p className="mt-1 text-sm">
                <span className="font-medium">{booking.linkedYacht.name}</span>
                {operator?.name && <span className="text-slate-500"> · {operator.name}</span>}
                {booking.startLocation?.names && (
                  <span className="text-slate-500"> · {extractFromMultiName(booking.startLocation.names)}</span>
                )}
                <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                  {booking.yachtLinkMethod === "auto_confident"
                    ? "auto-linked: exact yacht + operator match"
                    : `linked${booking.yachtLinkedBy ? ` by ${booking.yachtLinkedBy}` : ""}`}
                </span>
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-500">
                Not linked{booking.yachtLinkMethod === "declined" ? " (a previous link was removed)" : ""}. Link the
                yacht to show boarding information and good to know.
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {booking.linkedYacht && canConvert && (
              <Button size="sm" disabled={converting} onClick={convert} title="Build the full booking page: offer + yacht record, money from HubSpot, payments as per Xero">
                {converting ? "Converting…" : "Convert to full booking"}
              </Button>
            )}
            <Button size="sm" variant={booking.linkedYacht ? "outline" : "default"} onClick={() => setLinkOpen(true)}>
              {booking.linkedYacht ? "Change" : "Link yacht & base"}
            </Button>
            {booking.linkedYacht && (
              <button
                className="text-xs text-slate-500 hover:text-red-600 hover:underline disabled:opacity-50"
                disabled={unlinking}
                onClick={unlink}
              >
                Unlink
              </button>
            )}
          </div>
        </div>
      </div>
      {booking.linkedYacht && (
        <>
          <BoardingSection booking={booking} operator={operator} />
          <GoodToKnowSection booking={booking} operator={operator} />
        </>
      )}
      <h2 className="mt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Xero</h2>
      <XeroSection bookingId={booking.id} />
      {linkOpen && (
        <LinkYachtDialog
          bookingId={booking.id}
          canConvert={canConvert}
          onClose={() => setLinkOpen(false)}
          onLinked={() => {
            setLinkOpen(false);
            refresh();
          }}
          onConverted={() => {
            setLinkOpen(false);
            refresh();
            queryClient.invalidateQueries();
          }}
        />
      )}
    </div>
            </>
          )}
          {importedTab === "confirm" && <ConfirmTab c={importedConfirmation} bookingId={booking.id} />}
          {importedTab === "money" && <XeroSection bookingId={booking.id} />}
          {importedTab === "commission" && <CommissionSection booking={booking} />}
          {importedTab === "contract" && (
            <>
              <ContractSection booking={booking} />
              <DocumentsSection bookingId={booking.id} hasDeal={!!booking.hubspotId} />
              {importedProfile.needsBankDetails && <SupplierBankSection bookingId={booking.id} supplierLabel={importedProfile.supplierLabel} />}
            </>
          )}
          {importedTab === "ops" && (
            <>
              {importedProfile.showCharterOps && <CharterOpsSection bookingId={booking.id} hasDeal={!!booking.hubspotId} />}
              {importedProfile.showPreferences && <PreferencesSection bookingId={booking.id} hasDeal={!!booking.hubspotId} />}
            </>
          )}
          {importedTab === "comms" && <CommsSection bookingId={booking.id} />}
          {importedTab === "review" && <ReviewSection bookingId={booking.id} />}
          {importedTab === "timeline" && <TimelineSection bookingId={booking.id} />}
        </div>
        <div className="xl:sticky xl:top-0">
          <BookingRail booking={booking} operatorName={operator?.name} supplierLabel={importedProfile.supplierLabel} onGo={importedGo} />
        </div>
      </div>
      </div>
    </div>
  );
}

const queryClient = new QueryClient();

const getBookingByYachtOfferData = async (bookingId: string) => {
  return fetch(`/api/bookings/offer-yacht/${bookingId}`);
};

export function bookingLoader({ params: { id } }: { params: any }) {
  return queryClient.fetchQuery({
    queryKey: ["bookingYachtOfferData", id],
    queryFn: () => getBookingByYachtOfferData(id),
  });
}

export function operatorGoodToKnowMapper(charterType: string, goodToKnow: GoodToKnow) {
  switch (goodToKnow.type) {
    case "charter-dependent":
      let res = null;
      if (charterType === "skippered") {
        res = goodToKnow.skippered;
      } else if (charterType === "crewed") {
        res = goodToKnow.crewed;
      } else if (charterType === "bareboat") {
        res = goodToKnow.bareboat;
      }
      return res;
    case "simple":
      return goodToKnow.simple;
    default:
      return "Good to know type not supported";
  }
}

export function findBoardingInformationForLocation(locationId: string | undefined, boardingInfos: BoardingInfo[]) {
  const res = boardingInfos.find((info) => info.location.id === locationId);

  return res;
}
